import React, { useContext, useEffect } from 'react'
import { InputHolderDocument } from './InputHolderDocument'
import { AuthenticationContext } from 'app/Contexts/AuthenticationProvider'
import { InputRegistryDocument } from './InputRegistryDocument'
import { StepSuccess } from './StepSuccess'
import { Button } from 'app/components/ui/Button'
import { Text } from 'app/components/ui/Text'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { routes } from 'app/routes'
import { Title } from 'app/components/ui/Title'
import { Container } from 'app/components/ui/Container'
import { Base } from 'app/components/ui/Base'
import { StepCard } from './StepCard'
import { themeEstra } from 'app/styles/themeEstra'
import ArrowIcon from 'static/icons/step_arrow.svg'
import { ProfileStepsNav } from 'app/components/shared/ProfileStepsNav'
import { ApiErrorMessage } from 'app/components/ui/form/ApiErrorMessage'
import { endpoints } from 'app/api/endpoints'
import { useGet } from 'restful-react'
import { ApiError } from 'EstraSuppliers'
import { downloadBlobFile } from 'app/utils/file'

interface Props {
  onStepsCompleted: () => void
  onReUploadDocumentRequest?: () => void
  stepRefFoward?: React.RefObject<HTMLDivElement>
}

export const Steps: React.FC<Props> = ({
  onStepsCompleted,
  onReUploadDocumentRequest,
  stepRefFoward,
}) => {
  const authCtx = useContext(AuthenticationContext)
  const authToken = authCtx?.authToken || ''

  const { push } = useHistory()

  // API: Get registry PDF
  const {
    data: registryPdf,
    loading: isLoadingRegistryPdf,
    error: registryPdfError,
    refetch: getRegistryPdf,
  } = useGet<Promise<Blob>, ApiError>({
    path: endpoints.registryPdf,
    lazy: true,
    resolve: file => file && file.blob(),
    requestOptions: () => ({
      headers: {
        'Content-Type': 'application/pdf',
        Authorization: authToken,
      },
    }),
  })

  // Download file when retrieved from the API
  useEffect(() => {
    if (registryPdf) {
      registryPdf.then(blobFile =>
        downloadBlobFile(blobFile, 'anagrafica_fornitore_estra.pdf')
      )
    }
  }, [registryPdf])

  const holderUploadedFile =
    authCtx && authCtx.actor
      ? {
          name: authCtx.actor.supplier.identity_document_file_name,
          url: authCtx.actor.supplier.identity_document_url,
        }
      : null

  const pdfRegistryFile =
    authCtx && authCtx.actor
      ? {
          name: authCtx.actor.supplier.pdf_registry_file_name,
          url: authCtx.actor.supplier.pdf_registry_url,
        }
      : null

  const StepsText =
    'In questa pagina ti chiediamo di fare una foto al fronte e al retro della carta d’identità del titolare e caricarla sul sistema.\n' +
    'Poi dovrai scaricare il modulo anagrafica che hai compilato, stamparlo, firmarlo e caricarlo con l’apposito modulo.\n' +
    'Questa operazione può essere fatta in più momenti, basta che ogni volta premi il pulsante SALVA e la prossima volta che rientrerai troverai il modulo all’esatto step che hai lasciato.\n\n' +
    'Se ti accorgi di aver sbagliato un dato inserito nel modulo di anagrafica, usa il pulsante qui sotto per modificarlo.'

  return (
    <>
      {/* HEADER */}

      <Container>
        <ProfileStepsNav currentStep={'documents'}></ProfileStepsNav>
        <StepsHeaderWrapper>
          <Text variant='s24' mb={2}>
            {StepsText}
          </Text>
          <Button
            variant='ciano'
            size='lg'
            onClick={() => push(routes.editProfile.makeUrl())}
          >
            MODIFICA L&apos;ANAGRAFICA
          </Button>
        </StepsHeaderWrapper>
      </Container>

      {/* STEP 1 */}

      {holderUploadedFile && holderUploadedFile.url ? (
        <Container>
          <StepWrapper>
            <StepCard title="Documento d'identità del titolare">
              <StepSuccess />
              <a onClick={onReUploadDocumentRequest}>
                <Text
                  variant='s12'
                  weight='400'
                  color={themeEstra.color.neutri.grigio}
                >
                  MODIFICA
                </Text>
              </a>
            </StepCard>
          </StepWrapper>
        </Container>
      ) : (
        <>
          <Base
            paddingTop={8}
            bgImage='https://res.cloudinary.com/estra/image/upload/v1612531114/estra20dev/area_fornitori/signup/signup_step_bg.svg'
          >
            <InitialStepWrapper>
              <InitialStepTitle size={3}>
                Pronto? {'\n'}
                Iniziamo con il caricamento della foto del documento d’identità
                del titolare
              </InitialStepTitle>
              <InputHolderDocument />
            </InitialStepWrapper>
          </Base>
        </>
      )}

      <StepsWrapper>
        {/* STEP 2 */}
        {holderUploadedFile && holderUploadedFile.url ? (
          <StepWrapper>
            <img src={ArrowIcon} alt='step'></img>

            <div ref={stepRefFoward} />
            <DownloadButtonWrapper>
              <Button
                color='primary'
                disabled={isLoadingRegistryPdf}
                onClick={() => {
                  getRegistryPdf()
                }}
              >
                {isLoadingRegistryPdf
                  ? 'CARICAMENTO...'
                  : "SCARICA L'ANAGRAFICA E FIRMALA"}
              </Button>
              {registryPdfError && (
                <ApiErrorMessage
                  error={registryPdfError}
                  mt={0.5}
                ></ApiErrorMessage>
              )}
            </DownloadButtonWrapper>
            <img src={ArrowIcon} alt='step'></img>
          </StepWrapper>
        ) : null}

        {/* STEP 3 */}
        {holderUploadedFile && holderUploadedFile.url ? (
          <StepWrapper>
            <StepCard title='Carica il file di anagrafica firmato'>
              <Text variant='s14' mb={2}>
                {"Attenzione: quando scansioni e ricarichi il PDF firmato, accertati che sia il documento completo,\n" +
                "contenente sia tutte le pagine della scheda anagrafica\n" +
                "che la scansione del documento d'identità del titolare come ultima pagina."}
              </Text>
              {pdfRegistryFile && pdfRegistryFile.url ? (
                <StepSuccess />
              ) : holderUploadedFile && holderUploadedFile.url ? (
                <InputRegistryDocument
                  onFileUploadedSuccess={() => {
                    onStepsCompleted()
                  }}
                />
              ) : (
                <div>Completa lo step precedente</div>
              )}
            </StepCard>
          </StepWrapper>
        ) : null}
      </StepsWrapper>
    </>
  )
}

const StepsHeaderWrapper = styled.div`
  max-width: 900px;
  margin-right: auto;
  margin-left: auto;

  text-align: center;

  margin-bottom: 6rem;
`

const InitialStepWrapper = styled(Container)`
  text-align: center;
`

const InitialStepTitle = styled(Title)`
  max-width: 900px;
  margin-right: auto;
  margin-left: auto;

  margin-bottom: 3rem;
`

const StepsWrapper = styled(Container)``

const StepWrapper = styled.div`
  margin-bottom: 3rem;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
`

const DownloadButtonWrapper = styled.div`
  margin-top: 3rem;
  margin-bottom: 3rem;
`
